import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import { bonusAdminEnvelope } from "./bonusAdminBody";
import { getSeriesQueryKey } from "./useGetSeries";
import { LIST_SERIES_QUERY_KEY } from "./useListSeries";
import type { SeriesIn, SeriesOut } from "../../components/Promotions/DynamicBonuses/types";

type StoredToken = { AccessToken?: string };

type UpdateSeriesVars = { seriesId: string; payload: SeriesIn };

type UpdateSeriesResult = { body: SeriesOut; message?: string };

async function putSeries({ seriesId, payload }: UpdateSeriesVars): Promise<UpdateSeriesResult> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = API_ROUTES.UPDATE_DYNAMIC_BONUS_SERIES.replace("{series_id}", seriesId);
  const response = await instance.put(url, payload);
  const envelope = bonusAdminEnvelope<SeriesOut>(response);
  if (!envelope.body) {
    throw new Error("Series update returned no data");
  }
  return { body: envelope.body, message: envelope.message };
}

export const useUpdateSeries = (seriesId: string) => {
  const queryClient = useQueryClient();

  return useMutation<UpdateSeriesResult, Error, SeriesIn>({
    mutationFn: (payload) => putSeries({ seriesId, payload }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: LIST_SERIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: getSeriesQueryKey(seriesId) });
      message.success(result.message || AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED);
    },
    onError: (error) => {
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    },
  });
};
